import { CustomerSupportRequests, OrderSupportDialog } from "./order-support";
import { formatRecordDate } from "@taunggyi-plus/design-system";
import { useOrderRefresh } from "@taunggyi-plus/auth";
import { ActionIcon } from "@taunggyi-plus/design-system";
import { useFeedback } from "@taunggyi-plus/auth";
import { CustomerDeliveryRequest } from "@taunggyi-plus/delivery-feature";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@taunggyi-plus/auth";
import { Card, StatusPill, PaginatedList } from "@taunggyi-plus/design-system";
import type { Locale } from "@taunggyi-plus/domain";

type OrderKind = "all" | "stay" | "food" | "delivery" | "taxi" | "wash" | "night";
type OrderState = "all" | "active" | "completed" | "cancelled";

interface HotelBooking {
  id: string;
  property_id: string;
  room_type_id: string;
  status: "pending" | "confirmed" | "rejected" | "cancelled";
  check_in: string;
  check_out: string;
  room_count: number;
  guest_count: number;
  response_note: string;
  created_at: string;
}

interface MerchantOrder {
  id: string;
  shop_id: string;
  status: "pending" | "accepted" | "preparing" | "ready" | "rejected" | "cancelled" | "completed";
  total_minor: number;
  delivery_address: string;
  response_note: string;
  created_at: string;
}

interface TaxiOrder {
  id: string;
  group_id: string;
  cancellation_reason: string;
  status:
    "pending" | "confirmed" | "assigned" | "in_progress" | "completed" | "cancelled" | "rejected";
  pickup_area: string;
  dropoff_area: string;
  requested_pickup_at: string | null;
  fare_minor: number;
  note: string;
  created_at: string;
}

interface DirectRequest {
  id: string;
  status: string;
  pickup: string;
  dropoff: string;
  recipient: string;
  phone: string;
  note: string;
  response: string;
  quote_minor: number | null;
  created_at: string;
  job_status?: string;
}

interface DeliveryReference {
  direct_request_id?: string;
  merchant_order_id: string;
  status: string;
}

interface NamedResource {
  id: string;
  display_name?: string;
  name?: string;
}

interface OrderContact {
  kind: "stay" | "food" | "taxi";
  order_id: string;
  name: string;
  phone: string | null;
  contact_type: "driver" | "provider";
}

function OrderContactLink({ contact, locale }: { contact?: OrderContact; locale: Locale }) {
  const phone = contact?.phone?.trim();
  const dialNumber = phone?.replace(/[^+0-9]/g, "");
  if (contact?.kind === "taxi") return null;
  if (!contact || !phone || !dialNumber || !/^\+?\d{5,15}$/.test(dialNumber)) return null;
  const labels = {
    en: { driver: "Call driver", provider: "Call provider" },
    my: { driver: "ယာဉ်မောင်းထံ ဖုန်းခေါ်ရန်", provider: "ဝန်ဆောင်မှုပေးသူထံ ဖုန်းခေါ်ရန်" },
    shn: { driver: "Call driver", provider: "Call provider" },
    zh: { driver: "致电司机", provider: "致电服务商" },
  };
  return (
    <a
      className="button button--secondary customer-order-call"
      href={`tel:${dialNumber}`}
      aria-label={`${labels[locale][contact.contact_type]}: ${contact.name}, ${phone}`}
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m7 3 3 5-2 2c1 3 3 5 6 6l2-2 5 3v3c0 1-1 2-2 2C10 21 3 14 2 5c0-1 1-2 2-2h3Z" />
      </svg>
      <span>
        {labels[locale][contact.contact_type]} · {phone}
      </span>
    </a>
  );
}

const words = {
  en: {
    assigned: "Driver assigned",
    in_progress: "Ride in progress",
    farePending: "Fare to be confirmed",
    pickupTime: "Pickup",
    title: "Orders",
    intro: "Track every stay request, food order, and delivery update in one place.",
    all: "All",
    stays: "Link Stay",
    food: "Link Food",
    active: "Active",
    completed: "Completed",
    cancelled: "Cancelled",
    empty: "No orders match these filters yet.",
    stay: "Stay request",
    foodOrder: "Food order",
    created: "Placed",
    pending: "Waiting for confirmation",
    confirmed: "Confirmed",
    accepted: "Accepted",
    preparing: "Preparing",
    ready: "Ready",
    rejected: "Rejected",
    delivered: "Delivered",
    delivery: "Delivery",
    cancel: "Cancel",
    cancelling: "Cancelling…",
    cancelledSaved: "Order cancelled.",
    filter: "Filter",
    orderType: "Order type",
    status: "Status",
    applyFilters: "Apply filters",
    resetFilters: "Reset",
    closeFilters: "Close",
    browse: "Browse services",
    guests: "guests",
    rooms: "rooms",
  },
  my: {
    assigned: "ယာဉ်မောင်း သတ်မှတ်ပြီး",
    in_progress: "ခရီးစဉ် ဆောင်ရွက်နေသည်",
    farePending: "ယာဉ်စီးခ အတည်ပြုရန်",
    pickupTime: "ကြိုမည့်အချိန်",
    title: "မှာယူမှုများ",
    intro:
      "တည်းခိုခန်းတောင်းဆိုမှု၊ အစားအသောက်မှာယူမှုနှင့် ပို့ဆောင်မှုအခြေအနေအားလုံးကို တစ်နေရာတည်းတွင် ကြည့်ပါ။",
    all: "အားလုံး",
    stays: "Link Stay",
    food: "Link Food",
    active: "လုပ်ဆောင်နေဆဲ",
    completed: "ပြီးဆုံး",
    cancelled: "ပယ်ဖျက်",
    empty: "ဤစစ်ထုတ်မှုနှင့်ကိုက်ညီသော မှာယူမှုမရှိသေးပါ။",
    stay: "တည်းခိုခန်းတောင်းဆိုမှု",
    foodOrder: "အစားအသောက်မှာယူမှု",
    created: "မှာယူပြီး",
    pending: "အတည်ပြုရန် စောင့်နေသည်",
    confirmed: "အတည်ပြုပြီး",
    accepted: "လက်ခံပြီး",
    preparing: "ပြင်ဆင်နေသည်",
    ready: "အသင့်ဖြစ်ပြီ",
    rejected: "ငြင်းပယ်ပြီး",
    delivered: "ပို့ဆောင်ပြီး",
    delivery: "ပို့ဆောင်မှု",
    cancel: "ပယ်ဖျက်မည်",
    cancelling: "ပယ်ဖျက်နေသည်…",
    cancelledSaved: "မှာယူမှု ပယ်ဖျက်ပြီးပါပြီ။",
    filter: "စစ်ထုတ်ရန်",
    orderType: "မှာယူမှုအမျိုးအစား",
    status: "အခြေအနေ",
    applyFilters: "စစ်ထုတ်မည်",
    resetFilters: "ပြန်သတ်မှတ်ရန်",
    closeFilters: "ပိတ်ရန်",
    browse: "ဝန်ဆောင်မှုများကြည့်ရန်",
    guests: "ဧည့်သည်",
    rooms: "အခန်း",
  },
  shn: {
    assigned: "Driver assigned",
    in_progress: "Ride in progress",
    farePending: "Fare to be confirmed",
    pickupTime: "Pickup",
    title: "ၶေႃႈသင်ႇ",
    intro: "တူၺ်းၶေႃႈယွၼ်းလိုဝ်ႈသဝ်း၊ သင်ႇတၢင်းၵိၼ်လႄႈၵၢၼ်သူင်ႇတင်းမူတ်းတီႈလဵဝ်။",
    all: "တင်းမူတ်း",
    stays: "Link Stay",
    food: "Link Food",
    active: "တိုၵ်ႉႁဵတ်း",
    completed: "ယဝ်ႉတူဝ်ႈ",
    cancelled: "ယႃႉယဝ်ႉ",
    empty: "ပႆႇမီးၶေႃႈသင်ႇဢၼ်ၶဝ်ႈၵၼ်။",
    stay: "ၶေႃႈယွၼ်းလိုဝ်ႈသဝ်း",
    foodOrder: "ၶေႃႈသင်ႇတၢင်းၵိၼ်",
    created: "သင်ႇယဝ်ႉ",
    pending: "တိုၵ်ႉပႂ်ႉယိုၼ်ယၼ်",
    confirmed: "ယိုၼ်ယၼ်ယဝ်ႉ",
    accepted: "ႁပ်ႉယဝ်ႉ",
    preparing: "တိုၵ်ႉႁၢင်ႈႁႅၼ်း",
    ready: "ႁၢင်ႈႁႅၼ်းယဝ်ႉ",
    rejected: "ပၢၵ်ႈပႅတ်ႈယဝ်ႉ",
    delivered: "သူင်ႇယဝ်ႉ",
    delivery: "ၵၢၼ်သူင်ႇ",
    cancel: "ယႃႉ",
    cancelling: "တိုၵ်ႉယႃႉ…",
    cancelledSaved: "ယႃႉၶေႃႈသင်ႇယဝ်ႉ။",
    filter: "Filter",
    orderType: "Order type",
    status: "Status",
    applyFilters: "Apply filters",
    resetFilters: "Reset",
    closeFilters: "Close",
    browse: "တူၺ်းဝန်ဆောင်ႈ",
    guests: "ၶႅၵ်ႇ",
    rooms: "ႁွင်ႈ",
  },
  zh: {
    assigned: "已分配司机",
    in_progress: "行程进行中",
    farePending: "车费待确认",
    pickupTime: "接车时间",
    title: "订单",
    intro: "在一个页面跟踪住宿申请、餐饮订单和配送进度。",
    all: "全部",
    stays: "Link Stay",
    food: "Link Food",
    active: "进行中",
    completed: "已完成",
    cancelled: "已取消",
    empty: "暂无符合筛选条件的订单。",
    stay: "住宿申请",
    foodOrder: "餐饮订单",
    created: "已下单",
    pending: "等待确认",
    confirmed: "已确认",
    accepted: "已接单",
    preparing: "准备中",
    ready: "可取餐",
    rejected: "已拒绝",
    delivered: "已送达",
    delivery: "配送",
    cancel: "取消",
    cancelling: "正在取消…",
    cancelledSaved: "订单已取消。",
    filter: "筛选",
    orderType: "订单类型",
    status: "状态",
    applyFilters: "应用筛选",
    resetFilters: "重置",
    closeFilters: "关闭",
    browse: "浏览服务",
    guests: "位客人",
    rooms: "间房",
  },
} as const;

function stateGroup(status: string): Exclude<OrderState, "all"> {
  if (["completed", "delivered"].includes(status)) return "completed";
  if (["cancelled", "rejected", "failed"].includes(status)) return "cancelled";
  return "active";
}

export function CustomerOrders({ locale }: { readonly locale: Locale }) {
  const auth = useAuth();
  const text = words[locale];
  const [bookings, setBookings] = useState<HotelBooking[]>([]);
  const [foodOrders, setFoodOrders] = useState<MerchantOrder[]>([]);
  const [taxiOrders, setTaxiOrders] = useState<TaxiOrder[]>([]);
  const [taxiGroups, setTaxiGroups] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [deliveries, setDeliveries] = useState<Map<string, string>>(new Map());
  const [properties, setProperties] = useState<Map<string, string>>(new Map());
  const [shops, setShops] = useState<Map<string, string>>(new Map());
  const [contacts, setContacts] = useState<Map<string, OrderContact>>(new Map());
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<OrderKind>("all");
  const [state, setState] = useState<OrderState>("all");
  const [requestOpen, setRequestOpen] = useState(false);
  const [directRequests, setDirectRequests] = useState<DirectRequest[]>([]);
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get("tab") === "requests" ? "requests" : "orders",
  );
  function changeTab(value: string) {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", value);
    url.searchParams.delete("list");
    url.searchParams.delete("record");
    window.history.replaceState(null, "", url);
    setTab(value);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
  const [support, setSupport] = useState<string | null>(null);
  const [requestVersion, setRequestVersion] = useState(0);
  const filterDialog = useRef<HTMLDialogElement>(null);
  const [draftQuery, setDraftQuery] = useState("");
  const [draftKind, setDraftKind] = useState<OrderKind>("all");
  const [draftState, setDraftState] = useState<OrderState>("all");
  const [busyId, setBusyId] = useState("");
  const [message, setMessage] = useFeedback();

  const load = useCallback(async () => {
    if (!auth.client || !auth.user || tab === "requests") return;
    const [
      bookingResult,
      orderResult,
      requestResult,
      jobResult,
      propertyResult,
      shopResult,
      taxiResult,
      taxiGroupResult,
      contactResult,
      directResult,
    ] = await Promise.all([
      auth.client
        .from("hotel_booking_requests")
        .select(
          "id,property_id,room_type_id,status,check_in,check_out,room_count,guest_count,response_note,created_at",
        )
        .order("created_at", { ascending: false }),
      auth.client
        .from("merchant_orders")
        .select("id,shop_id,status,total_minor,delivery_address,response_note,created_at")
        .order("created_at", { ascending: false }),
      auth.client.from("delivery_requests").select("merchant_order_id,status"),
      auth.client.from("delivery_jobs").select("merchant_order_id,direct_request_id,status"),
      auth.client.from("hotel_properties").select("id,display_name"),
      auth.client.from("merchant_shops").select("id,display_name"),
      auth.client
        .from("taxi_orders")
        .select(
          "id,group_id,status,pickup_area,dropoff_area,requested_pickup_at,fare_minor,note,cancellation_reason,created_at",
        )
        .eq("customer_user_id", auth.user.id)
        .order("created_at", { ascending: false }),
      auth.client.from("taxi_groups").select("id,display_name"),
      auth.client.rpc("customer_order_contacts"),
      auth.client
        .from("direct_delivery_requests")
        .select("id,status,pickup,dropoff,recipient,phone,note,response,quote_minor,created_at")
        .eq("customer_user_id", auth.user.id)
        .order("created_at", { ascending: false }),
    ]);
    setLoading(false);
    const error =
      bookingResult.error ??
      orderResult.error ??
      requestResult.error ??
      jobResult.error ??
      taxiResult.error ??
      directResult.error;
    if (error) {
      setMessage(error.message);
      return;
    }
    setContacts(
      new Map(
        ((contactResult.data ?? []) as OrderContact[]).map((contact) => [
          `${contact.kind}:${contact.order_id}`,
          contact,
        ]),
      ),
    );
    const jobs = (jobResult.data ?? []) as DeliveryReference[];
    setDirectRequests(
      ((directResult.data ?? []) as DirectRequest[]).map((r) => ({
        ...r,
        job_status: jobs.find((j) => j.direct_request_id === r.id)?.status,
      })),
    );
    setBookings((bookingResult.data ?? []) as HotelBooking[]);
    setFoodOrders((orderResult.data ?? []) as MerchantOrder[]);
    setTaxiOrders((taxiResult.data ?? []) as TaxiOrder[]);
    setTaxiGroups(
      new Map(
        ((taxiGroupResult.data ?? []) as NamedResource[]).map((group) => [
          group.id,
          group.display_name ?? "Link Ride",
        ]),
      ),
    );
    const nextDeliveries = new Map<string, string>();
    for (const request of (requestResult.data ?? []) as DeliveryReference[])
      nextDeliveries.set(request.merchant_order_id, request.status);
    for (const job of (jobResult.data ?? []) as DeliveryReference[])
      nextDeliveries.set(job.merchant_order_id, job.status);
    setDeliveries(nextDeliveries);
    setProperties(
      new Map(
        ((propertyResult.data ?? []) as NamedResource[]).map((item) => [
          item.id,
          item.display_name ?? text.stay,
        ]),
      ),
    );
    setShops(
      new Map(
        ((shopResult.data ?? []) as NamedResource[]).map((item) => [
          item.id,
          item.display_name ?? text.foodOrder,
        ]),
      ),
    );
    setMessage("");
  }, [auth.client, auth.user, tab, setMessage, text.foodOrder, text.stay]);

  useOrderRefresh(load);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  async function cancel(id: string, target: "stay" | "food") {
    if (!auth.client) return;
    setBusyId(id);
    const result = await auth.client.rpc(
      target === "stay" ? "cancel_hotel_booking" : "cancel_merchant_order",
      target === "stay" ? { booking_id: id } : { order_id: id },
    );
    setBusyId("");
    if (result.error) setMessage(result.error.message);
    else {
      setMessage(text.cancelledSaved);
      await load();
    }
  }

  const visibleBookings = useMemo(
    () =>
      bookings.filter(
        (booking) =>
          [...Object.values(booking), properties.get(booking.property_id)]
            .join(" ")
            .toLowerCase()
            .includes(query.trim().toLowerCase()) &&
          (kind === "all" || kind === "stay") &&
          (state === "all" || stateGroup(booking.status) === state),
      ),
    [bookings, kind, state, query, properties],
  );
  const visibleFoodOrders = useMemo(
    () =>
      foodOrders.filter((order) => {
        const effectiveStatus = deliveries.get(order.id) ?? order.status;
        return (
          [...Object.values(order), shops.get(order.shop_id)]
            .join(" ")
            .toLowerCase()
            .includes(query.trim().toLowerCase()) &&
          (kind === "all" ||
            kind === "food" ||
            (kind === "delivery" && deliveries.has(order.id))) &&
          (state === "all" || stateGroup(effectiveStatus) === state)
        );
      }),
    [deliveries, foodOrders, kind, state, query, shops],
  );

  const visibleTaxiOrders = useMemo(
    () =>
      taxiOrders.filter((order) => {
        const rideState =
          order.status === "completed"
            ? "completed"
            : order.status === "cancelled" || order.status === "rejected"
              ? "cancelled"
              : "active";
        return (
          [...Object.values(order), taxiGroups.get(order.group_id)]
            .join(" ")
            .toLowerCase()
            .includes(query.trim().toLowerCase()) &&
          (kind === "all" || kind === "taxi") &&
          (state === "all" || state === rideState)
        );
      }),
    [taxiOrders, kind, state, query, taxiGroups],
  );

  const visibleDirectRequests = directRequests.filter(
    (r) =>
      (kind === "all" || kind === "delivery") &&
      (state === "all" || stateGroup(r.job_status ?? r.status) === state) &&
      Object.values(r).join(" ").toLowerCase().includes(query.trim().toLowerCase()),
  );
  async function respondDelivery(id: string, decision: string) {
    if (!auth.client || busyId) return;
    setBusyId(id);
    try {
      const result = await auth.client.rpc("respond_direct_delivery", {
        request: id,
        decision,
        quote: null,
        note: "",
      });
      if (result.error) setMessage(result.error.message);
      else await load();
    } finally {
      setBusyId("");
    }
  }
  if (tab === "requests" && new URLSearchParams(window.location.search).has("record"))
    return <CustomerSupportRequests />;
  return (
    <section className="customer-orders-page">
      <div
        className="customer-orders-actions customer-order-tabs"
        role="tablist"
        aria-label="Orders and requests"
      >
        {(
          [
            ["orders", "Orders"],
            ["requests", "Request"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            role="tab"
            aria-selected={tab === value}
            className={`button ${tab === value ? "" : "button--secondary"}`}
            onClick={() => changeTab(value)}
          >
            {label}
          </button>
        ))}
      </div>
      {support !== null && (
        <OrderSupportDialog
          initialOrder={support}
          onClose={() => setSupport(null)}
          onSaved={() => {
            setSupport(null);
            setRequestVersion((v) => v + 1);
            changeTab("requests");
          }}
        />
      )}
      {tab === "requests" ? (
        <>
          <CustomerSupportRequests key={requestVersion} onSupport={() => setSupport("")} />
        </>
      ) : (
        <>
          <div className="route-heading customer-orders-heading">
            <h1>{text.title}</h1>
            <div className="customer-orders-actions">
              <button
                type="button"
                className="toolbar-icon-button"
                aria-label="Order support"
                title="Order support"
                aria-haspopup="dialog"
                onClick={() => setSupport("")}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  aria-hidden="true"
                >
                  <path d="M4 13v-1a8 8 0 0 1 16 0v1M4 12H3v6h4v-6H4Zm16 0h1v6h-4v-6h3Zm0 6v1a2 2 0 0 1-2 2h-6" />
                </svg>
              </button>
              <button
                type="button"
                className="toolbar-icon-button"
                aria-label="Request delivery"
                title="Request delivery"
                aria-haspopup="dialog"
                onClick={() => setRequestOpen(true)}
              >
                <ActionIcon action="add" />
              </button>
              <button
                className="toolbar-icon-button"
                type="button"
                aria-label={text.filter}
                title={text.filter}
                data-filtered={kind !== "all" || state !== "all"}
                aria-haspopup="dialog"
                onClick={() => {
                  setDraftQuery(query);
                  setDraftKind(kind);
                  setDraftState(state);
                  filterDialog.current?.showModal();
                }}
              >
                <ActionIcon action="filter" />
              </button>
            </div>
          </div>
          {requestOpen && (
            <dialog
              ref={(node) => {
                if (node && !node.open) node.showModal();
              }}
              className="customer-order-filter-dialog subscription-popup"
              aria-labelledby="customer-delivery-request-title"
              onClose={() => setRequestOpen(false)}
            >
              <header>
                <h3 id="customer-delivery-request-title">Request delivery</h3>
                <button
                  type="button"
                  className="toolbar-icon-button"
                  aria-label="Close request"
                  onClick={() => setRequestOpen(false)}
                >
                  ×
                </button>
              </header>
              <CustomerDeliveryRequest
                onSubmitted={() => {
                  setRequestOpen(false);
                  void load();
                }}
              />
            </dialog>
          )}
          <dialog
            ref={filterDialog}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                event.currentTarget.close();
              }
            }}
            className="customer-order-filter-dialog"
            aria-labelledby="order-filter-title"
          >
            <form
              onSubmit={(event) => {
                event.preventDefault();
                setQuery(draftQuery);
                setKind(draftKind);
                setState(draftState);
                filterDialog.current?.close();
              }}
            >
              <header>
                <h2 id="order-filter-title">{text.filter}</h2>
                <button
                  className="button button--secondary"
                  type="button"
                  aria-label={text.closeFilters}
                  onClick={() => filterDialog.current?.close()}
                >
                  ×
                </button>
              </header>
              <label>
                Search orders
                <input
                  type="search"
                  value={draftQuery}
                  onChange={(event) => setDraftQuery(event.target.value)}
                />
              </label>
              <label>
                {text.orderType}
                <select
                  aria-label={text.orderType}
                  value={draftKind}
                  onChange={(event) => setDraftKind(event.target.value as OrderKind)}
                >
                  <option value="all">{text.all}</option>
                  <option value="stay">{text.stays}</option>
                  <option value="food">{text.food}</option>
                  <option value="delivery">Link Delivery</option>
                  <option value="taxi">Link Ride</option>
                  <option value="wash">Link Wash</option>
                  <option value="night">Link Night</option>
                </select>
              </label>
              <label>
                {text.status}
                <select
                  aria-label={text.status}
                  value={draftState}
                  onChange={(event) => setDraftState(event.target.value as OrderState)}
                >
                  {(["all", "active", "completed", "cancelled"] as const).map((value) => (
                    <option value={value} key={value}>
                      {text[value]}
                    </option>
                  ))}
                </select>
              </label>
              <footer>
                <button
                  className="button button--secondary"
                  type="button"
                  onClick={() => {
                    setDraftQuery("");
                    setDraftKind("all");
                    setDraftState("all");
                  }}
                >
                  {text.resetFilters}
                </button>
                <button className="button" type="submit">
                  {text.applyFilters}
                </button>
              </footer>
            </form>
          </dialog>
          {message && (
            <p className="form-message" role="status">
              {message}
            </p>
          )}
          <p className="management-toolbar__summary" aria-live="polite">
            {visibleBookings.length +
              visibleFoodOrders.length +
              visibleTaxiOrders.length +
              visibleDirectRequests.length}{" "}
            records
          </p>
          <div className="customer-order-timeline">
            <PaginatedList
              resetKey={`${query}:${kind}:${state}`}
              label="Order pages"
              items={[
                ...visibleDirectRequests.map((r) => (
                  <Card className="customer-order-record" key={r.id}>
                    <h3>
                      {r.pickup} → {r.dropoff}
                    </h3>
                    <StatusPill tone={r.job_status === "delivered" ? "success" : "neutral"}>
                      {(r.job_status ?? r.status).replaceAll("_", " ")}
                    </StatusPill>
                    <p>
                      Delivery · {r.recipient} · {r.phone}
                    </p>
                    <small>
                      #{r.id.slice(0, 8)} · {formatRecordDate(r.created_at, "en", true)}
                    </small>
                    {r.note && <p>{r.note}</p>}
                    {r.response && <p>{r.response}</p>}
                    {r.quote_minor !== null && (
                      <p>Delivery fee: {(r.quote_minor / 100).toLocaleString()} MMK</p>
                    )}
                    {r.status === "quoted" && (
                      <button
                        className="button"
                        disabled={!!busyId}
                        onClick={() => void respondDelivery(r.id, "accepted")}
                      >
                        Accept fee & request dispatch
                      </button>
                    )}
                    {["pending", "quoted"].includes(r.status) && (
                      <button
                        className="button button--danger"
                        disabled={!!busyId}
                        onClick={() => void respondDelivery(r.id, "cancelled")}
                      >
                        Cancel request
                      </button>
                    )}
                    <button
                      className="button button--secondary"
                      onClick={() => setSupport(`delivery:${r.id}`)}
                    >
                      Order support & review
                    </button>
                  </Card>
                )),
                ...visibleBookings.map((booking) => (
                  <Card className="customer-order-record" key={booking.id}>
                    <div className="customer-order-record__topline">
                      <span>
                        <small>{text.stay}</small>
                        <strong>{properties.get(booking.property_id) ?? text.stay}</strong>
                      </span>
                      <StatusPill
                        tone={
                          booking.status === "confirmed"
                            ? "success"
                            : ["cancelled", "rejected"].includes(booking.status)
                              ? "neutral"
                              : "warning"
                        }
                      >
                        {text[booking.status]}
                      </StatusPill>
                    </div>
                    <p>
                      {formatRecordDate(booking.check_in)} → {formatRecordDate(booking.check_out)} ·{" "}
                      {booking.room_count} {text.rooms} · {booking.guest_count} {text.guests}
                    </p>
                    <small>
                      #{booking.id.slice(0, 8).toUpperCase()} ·{" "}
                      {formatRecordDate(booking.created_at, "en", true)}
                    </small>
                    {booking.response_note && <p>{booking.response_note}</p>}
                    <OrderContactLink
                      contact={contacts.get(`stay:${booking.id}`)}
                      locale={locale}
                    />
                    <button
                      className="button button--secondary"
                      onClick={() => setSupport(`hotel:${booking.id}`)}
                    >
                      Order support & review
                    </button>
                    {booking.status === "pending" && (
                      <button
                        className="button button--danger"
                        disabled={busyId === booking.id}
                        onClick={() => void cancel(booking.id, "stay")}
                      >
                        {busyId === booking.id ? text.cancelling : text.cancel}
                      </button>
                    )}
                  </Card>
                )),
                ...visibleFoodOrders.map((order) => {
                  const deliveryStatus = deliveries.get(order.id);
                  return (
                    <Card className="customer-order-record" key={order.id}>
                      <div className="customer-order-record__topline">
                        <span>
                          <small>{text.foodOrder}</small>
                          <strong>{shops.get(order.shop_id) ?? text.foodOrder}</strong>
                        </span>
                        <StatusPill
                          tone={
                            order.status === "completed" || deliveryStatus === "delivered"
                              ? "success"
                              : ["cancelled", "rejected"].includes(order.status)
                                ? "neutral"
                                : "warning"
                          }
                        >
                          {text[order.status]}
                        </StatusPill>
                      </div>
                      <p>
                        {(order.total_minor / 100).toLocaleString()} MMK · {order.delivery_address}
                      </p>
                      <small>
                        #{order.id.slice(0, 8).toUpperCase()} ·{" "}
                        {formatRecordDate(order.created_at, "en", true)}
                      </small>
                      {deliveryStatus && (
                        <p>
                          <strong>{text.delivery}:</strong>{" "}
                          {deliveryStatus === "delivered" ? text.delivered : deliveryStatus}
                        </p>
                      )}
                      {order.response_note && <p>{order.response_note}</p>}
                      <OrderContactLink
                        contact={contacts.get(`food:${order.id}`)}
                        locale={locale}
                      />
                      <button
                        className="button button--secondary"
                        onClick={() => setSupport(`merchant:${order.id}`)}
                      >
                        Order support & review
                      </button>
                      {order.status === "pending" && (
                        <button
                          className="button button--danger"
                          disabled={busyId === order.id}
                          onClick={() => void cancel(order.id, "food")}
                        >
                          {busyId === order.id ? text.cancelling : text.cancel}
                        </button>
                      )}
                    </Card>
                  );
                }),
                ...visibleTaxiOrders.map((order) => (
                  <Card className="customer-order-record" key={order.id}>
                    <div className="customer-order-record__topline">
                      <span>
                        <small>Link Ride</small>
                        <strong>{taxiGroups.get(order.group_id) ?? "Link Ride"}</strong>
                      </span>
                      <StatusPill
                        tone={
                          order.status === "completed"
                            ? "success"
                            : ["cancelled", "rejected"].includes(order.status)
                              ? "neutral"
                              : "warning"
                        }
                      >
                        {text[order.status]}
                      </StatusPill>
                    </div>
                    <p>
                      {order.pickup_area} → {order.dropoff_area}
                    </p>
                    {order.requested_pickup_at && (
                      <p>
                        {text.pickupTime}: {formatRecordDate(order.requested_pickup_at, "en", true)}
                      </p>
                    )}
                    <p>
                      {Number(order.fare_minor) > 0
                        ? `${(Number(order.fare_minor) / 100).toLocaleString()} MMK`
                        : text.farePending}
                    </p>
                    <small>
                      #{order.id.slice(0, 8).toUpperCase()} ·{" "}
                      {formatRecordDate(order.created_at, "en", true)}
                    </small>
                    {order.note && <p>{order.note}</p>}
                    <OrderContactLink contact={contacts.get(`taxi:${order.id}`)} locale={locale} />
                    <button
                      className="button button--secondary"
                      onClick={() => setSupport(`taxi:${order.id}`)}
                    >
                      Order support & review
                    </button>
                    {order.cancellation_reason && (
                      <p>
                        <strong>Cancellation reason:</strong> {order.cancellation_reason}
                      </p>
                    )}
                  </Card>
                )),
              ]}
              renderItem={(item) => item}
            />
            {loading && <p role="status">Loading orders…</p>}
            {!loading &&
              !message &&
              !visibleBookings.length &&
              !visibleFoodOrders.length &&
              !visibleTaxiOrders.length &&
              !visibleDirectRequests.length && (
                <Card className="empty-state">
                  <strong>{text.empty}</strong>
                  <a className="button" href="/services">
                    {text.browse}
                  </a>
                </Card>
              )}
          </div>
        </>
      )}
    </section>
  );
}
